import { isSalesforceEmbedded } from '../config';

// Globals
export const SALESFORCE_EMBEDDED_ROOM_PATH_KEY = 'ept_salesforce_embedded_room_path';
const SALESFORCE_BOOTSTRAP_QUERY_KEYS = ['embedded', 'canvas_session_id', 'account_id'];
const SAFE_EXTERNAL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

// Models
type OpenTargetResult = {
  handled: boolean;
  openedUrl: string | null;
};

// Private methods
const _getWindow = (): Window | null => {
  if (typeof window === 'undefined') return null;
  return window;
};

const _isRoomPath = (pathname: string): boolean => /^\/room\/[^/]+\/?$/.test(pathname);

const _getUrl = (href: string): URL | null => {
  const currentWindow = _getWindow();
  if (!currentWindow) return null;

  try {
    return new URL(href, currentWindow.location.origin);
  } catch {
    return null;
  }
};

const _isSameDocumentAnchor = (url: URL): boolean => {
  const currentWindow = _getWindow();
  if (!currentWindow) return false;

  return (
    url.origin === currentWindow.location.origin &&
    url.pathname === currentWindow.location.pathname &&
    url.search === currentWindow.location.search &&
    Boolean(url.hash)
  );
};

const _isSafeUrl = (url: URL): boolean => {
  if (url.origin === _getWindow()?.location.origin) return true;
  return SAFE_EXTERNAL_PROTOCOLS.has(url.protocol);
};

const _sanitizeForNormalAuth = (url: URL): string => {
  const sanitizedUrl = new URL(url.toString());
  SALESFORCE_BOOTSTRAP_QUERY_KEYS.forEach(key => sanitizedUrl.searchParams.delete(key));
  return sanitizedUrl.toString();
};

const _getAnchorFromEvent = (event: MouseEvent): HTMLAnchorElement | null => {
  const target = event.target;
  if (!(target instanceof Element)) return null;
  return target.closest('a[href]');
};

// Public methods
export const rememberSalesforceEmbeddedRoomPath = (): string | null => {
  const currentWindow = _getWindow();
  if (!currentWindow) return null;

  const embeddedParam = new URLSearchParams(currentWindow.location.search).get('embedded');
  if (embeddedParam === 'salesforce' && _isRoomPath(currentWindow.location.pathname)) {
    sessionStorage.setItem(SALESFORCE_EMBEDDED_ROOM_PATH_KEY, currentWindow.location.pathname);
    return currentWindow.location.pathname;
  }

  const existingRoomPath = getSalesforceEmbeddedRoomPath();
  if (existingRoomPath) {
    return existingRoomPath;
  }
  return null;
};

export const getSalesforceEmbeddedRoomPath = (): string | null => {
  const currentWindow = _getWindow();
  if (!currentWindow) return null;

  return sessionStorage.getItem(SALESFORCE_EMBEDDED_ROOM_PATH_KEY);
};

export const isAllowedSalesforceEmbeddedPath = (pathname: string): boolean => {
  const embeddedRoomPath = rememberSalesforceEmbeddedRoomPath();
  return Boolean(embeddedRoomPath && pathname === embeddedRoomPath);
};

export const openSalesforceEmbeddedTarget = (href: string): OpenTargetResult => {
  if (!isSalesforceEmbedded()) {
    return { handled: false, openedUrl: null };
  }

  const url = _getUrl(href);
  if (!url || !_isSafeUrl(url)) {
    return { handled: true, openedUrl: null };
  }

  if (_isSameDocumentAnchor(url)) {
    return { handled: false, openedUrl: null };
  }

  if (isAllowedSalesforceEmbeddedPath(url.pathname)) {
    return { handled: false, openedUrl: null };
  }

  const openedUrl = _sanitizeForNormalAuth(url);
  window.open(openedUrl, '_blank', 'noopener,noreferrer');
  return { handled: true, openedUrl };
};

export const handleSalesforceEmbeddedAnchorClick = (event: MouseEvent): void => {
  if (event.defaultPrevented) return;

  const anchor = _getAnchorFromEvent(event);
  const href = anchor?.getAttribute('href')?.trim();
  if (!href || href === '#') return;

  const result = openSalesforceEmbeddedTarget(href);
  if (!result.handled) return;

  event.preventDefault();
  event.stopPropagation();
};
